import React, { useState } from 'react';
import { Eye, Edit, Trash2 } from 'lucide-react';
import DataTable from './DataTable';
import { AdminPageLayout } from './AdminPageLayout';

export function UserManagementTable({
  users = [],
  roles = [],
  onViewUser,
  onEditUser,
  onDeleteUser,
  onCreateUser,
  canReadUser = true,
  canUpdateUser = true,
  canDeleteUser = true,
  canCreateUser = true,
  loading = false,
  onSort,
  onFilter,
  onSearch,
  onPageChange,
  onPageSizeChange,
  currentPage = 1,
  totalPages = 1,
  totalCount = 0,
  pageSize = 10,
  className = ''
}) {
  const [selectedUsers, setSelectedUsers] = useState([]);

  const safeUsers = Array.isArray(users) ? users : [];

  // Define table columns
  const columns = [
    {
      key: 'name',
      label: 'Name',
      accessor: 'name',
      sortable: true,
      filterable: true
    },
    {
      key: 'email',
      label: 'Email',
      accessor: 'email',
      sortable: true,
      filterable: true
    },
    {
      key: 'role',
      label: 'Role',
      accessor: 'role',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: roles.map(role => ({
        value: role.name,
        label: role.name.charAt(0).toUpperCase() + role.name.slice(1)
      }))
    },
    {
      key: 'isActive',
      label: 'Status',
      accessor: 'isActive',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { value: 'true', label: 'Active' },
        { value: 'false', label: 'Inactive' }
      ],
      render: (value) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${value
          ? 'bg-success-50 text-success-700 ring-1 ring-inset ring-success-600/20'
          : 'bg-red-50 text-red-700 ring-1 ring-inset ring-red-600/20'
          }`}>
          {value ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      key: 'createdAt',
      label: 'Created',
      accessor: 'createdAt',
      sortable: true,
      filterable: true,
      type: 'date',
      filterType: 'date',
      render: (value) => value ? new Date(value).toLocaleDateString() : '-'
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      filterable: false,
      render: (_, user) => (
        <div className="flex items-center gap-0.5 opacity-60 group-hover:opacity-100 transition-opacity">
          {canReadUser && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onViewUser?.(user);
              }}
              className="p-1.5 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-md transition-colors"
              title="View user details"
              type="button"
            >
              <Eye className="h-3.5 w-3.5" />
            </button>
          )}
          {canUpdateUser && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEditUser?.(user);
              }}
              className="p-1.5 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-md transition-colors"
              title="Edit user"
              type="button"
            >
              <Edit className="h-3.5 w-3.5" />
            </button>
          )}
          {canDeleteUser && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDeleteUser?.(user._id);
              }}
              className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
              title="Delete user (cannot be undone)"
              type="button"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )
    }
  ];

  // No custom actions - Add User button removed as requested
  const customActions = null;

  return (
    <AdminPageLayout className={className}>
      <DataTable
        data={safeUsers}
        columns={columns}
        loading={loading}
        onSort={onSort}
        onFilter={onFilter}
        onSearch={onSearch}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        currentPage={currentPage}
        totalPages={totalPages}
        totalCount={totalCount}
        pageSize={pageSize}
        searchable={true}
        filterable={true}
        sortable={true}
        selectable={false}
        emptyMessage="No users found"
        showPagination={true}
        showSearch={true}
        showFilters={true}
        customActions={customActions}
        serverPagination={true}
        serverFiltering={true}
      />
    </AdminPageLayout>
  );
}
